import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/** 800px-wide card version generated beside each cover by scripts/make-thumbs.sh. */
const cardSrc = (src) => src.replace(/\.jpe?g$/i, ".card.jpeg");
/** Card widths from the .product-grid breakpoints: 1 column ≤380px, 2 columns ≤900px, else 3 columns capped at 1320px. */
const CARD_SIZES = "(max-width: 380px) 90vw, (max-width: 900px) 46vw, 420px";

/**
 * @param {{ products: Array<{ icon: string; name: string; description: string; image: string; slug?: string }>; showViewAll?: boolean; startVisible?: boolean }} props
 */
export default function ProductGrid({ products, showViewAll = false, startVisible = false }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const reveals = el.querySelectorAll(".reveal:not(.visible)");
    if (!reveals.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    reveals.forEach((r) => observer.observe(r));
    return () => observer.disconnect();
  }, [products, startVisible]);

  const delayClass = (i) => {
    const r = i % 3;
    if (r === 1) return " reveal-delay-1";
    if (r === 2) return " reveal-delay-2";
    return "";
  };

  return (
    <div ref={wrapRef}>
      <div className="product-grid">
        {products.map((p, i) => {
          const revealClass = startVisible ? "reveal visible" : "reveal";
          const cardClass = `product-card product-card--cover ${revealClass}${delayClass(i)}`;
          const cardBody = (
            <div className="product-img product-img--cover">
              <img
                className="product-img__photo"
                src={p.image}
                srcSet={`${cardSrc(p.image)} 800w, ${p.image} 1600w`}
                sizes={CARD_SIZES}
                alt={p.name}
                loading="lazy"
                decoding="async"
              />
              <div className="product-img__scrim" aria-hidden="true" />
              <div className="tilt-shine" />
              <div className="product-info product-info--overlay">
                <div className="product-name">{p.name}</div>
                <p className="product-desc">{p.description}</p>
              </div>
            </div>
          );

          return p.slug ? (
            <Link
              key={p.name}
              to={`/collection/${p.slug}`}
              className={`${cardClass} product-card--linked`}
              aria-label={`View ${p.name}`}
            >
              {cardBody}
            </Link>
          ) : (
            <div key={p.name} className={cardClass}>
              {cardBody}
            </div>
          );
        })}
      </div>
      {showViewAll && (
        <div className={`collection-preview-cta ${startVisible ? "reveal visible" : "reveal"}`}>
          <a href="/collection" className="btn-primary">
            <span>View full collection</span>
          </a>
        </div>
      )}
    </div>
  );
}
